import { faGooglePlay } from "@fortawesome/free-brands-svg-icons/faGooglePlay";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../../components/Button";
import { FontSize } from "../../../../components/Text";
import { useTheme } from "../../../../ThemeContext";
import { TestIDs } from "../../../../tests/types/testIds";
import { Breakpoints } from "../../../../theme/Breakpoints";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { QRCode } from "../../../../utils/local-antd";
import { ANDROID_APP_URL } from "../../DashboardUtils";
import {
  SetupCard,
  SetupCardActions,
  SetupCardDescription,
  SetupCardTitle,
} from "./DashboardSetupCard";

/** Spans whatever is left of its grid row, so the board never ends on a gap */
const AppCard = styled(SetupCard)<{ $span3: number; $span2: number }>`
  container-type: inline-size;
  grid-column: span ${({ $span3 }) => $span3};

  @media (max-width: ${Breakpoints.LARGE_DESKTOP}) {
    grid-column: span ${({ $span2 }) => $span2};
  }

  @media (max-width: ${Breakpoints.TABLET}) {
    grid-column: auto;
  }
`;

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.s};
  flex: 1;

  @container (min-width: 34rem) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${Spacing.l};
  }
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.s};
  flex: 1;
  min-width: 0;
  max-width: 32rem;
`;

const QrRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.m};
`;

const QrFrame = styled.div`
  flex: none;
  padding: ${Spacing.xs};
  border-radius: ${Rounded.lg};
  border: 1px solid ${({ theme }) => theme.sub};
  background-color: ${({ theme }) => theme.white};
`;

const QrHint = styled.span`
  max-width: 14rem;
  font-size: ${FontSize.s};
  color: ${({ theme }) => theme.darkerSub};
`;

type DashboardSetupAppCardProps = {
  span3: number;
  span2: number;
};

export const DashboardSetupAppCard: React.FC<DashboardSetupAppCardProps> = ({
  span3,
  span2,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  return (
    <AppCard
      $span3={span3}
      $span2={span2}
      data-testid={TestIDs.dashboard.setup.appCard}
    >
      <Layout>
        <Copy>
          <SetupCardTitle>{t("dashboard.setup.app.title")}</SetupCardTitle>
          <SetupCardDescription>
            {t("dashboard.setup.app.description")}
          </SetupCardDescription>
          <SetupCardActions>
            <Button
              icon={faGooglePlay}
              onClick={() => window.open(ANDROID_APP_URL, "_blank", "noopener")}
            >
              {t("dashboard.setup.app.cta")}
            </Button>
          </SetupCardActions>
        </Copy>

        <QrRow>
          <QrFrame>
            <QRCode
              value={ANDROID_APP_URL}
              size={96}
              bordered={false}
              color={theme.black}
              bgColor={theme.white}
            />
          </QrFrame>
          <QrHint>{t("dashboard.setup.app.scan_hint")}</QrHint>
        </QrRow>
      </Layout>
    </AppCard>
  );
};
